import { Component, AfterViewInit, ViewChild, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';

// Importaciones de servicios.
import { ProductService } from '../../../services/product.service';

// Importaciones de componentes.
import { ProductCreateDialogComponent } from '../../../components/product-create-dialog/product-create-dialog.component';
import { ProductUpdateDialogComponent } from '../../../components/product-update-dialog/product-update-dialog.component';
import { HostDeleteDialogComponent } from '../../../components/host-delete-dialog/host-delete-dialog.component';

// Importaciones de interfaces.
import { IProduct } from '../../../interfaces/product.interface';
import { IProductResponse } from '../../../interfaces/product-response.interface';

// Importaciones de modelos.
import { Product } from '../../../models/product.model';
import { ProductDeleteDialogComponent } from '../../../components/product-delete-dialog/product-delete-dialog.component';

/**
 * Componente de configuración de los productos.
 *
 * Este componente proporciona una interfaz para gestionar la configuración de los productos dentro de la aplicación.
 * Permite realizar ajustes relacionados con los productos, como actualizar información, eliminar o crear.
 */
@Component({
  selector: 'app-product-settings',
  templateUrl: './product-settings.component.html',
  standalone: false,
})
export class ProductSettingsComponent implements OnInit, AfterViewInit {
  /**
   * Indica si los productos están siendo cargados.
   *
   * Esta propiedad se utiliza para mostrar un indicador de carga mientras
   * se recuperan los productos de la fuente de datos.
   */
  loading: boolean = false;

  /**
   * Columnas que se mostrarán en la tabla de los productos.
   *
   * Esta propiedad define las columnas que se mostrarán en la tabla de los productos, incluyendo los campos
   * relacionados con la información básica del producto.
   */
  tableColumns: string[] = [
    'name',
    'description',
    'image',
    'disabled',
    'updatedAt',
    'createdAt',
    'actions',
  ];

  /**
   * Datos de la tabla de los productos.
   *
   * Esta propiedad contiene los datos de los productos que se mostrarán en la tabla, utilizando `MatTableDataSource`
   * para manejar y mostrar los datos de manera eficiente, incluyendo funcionalidades como paginación y filtrado.
   */
  tableData: MatTableDataSource<IProduct> = new MatTableDataSource<IProduct>();

  /**
   * Paginador de la tabla de los productos.
   *
   * Esta propiedad se utiliza para la paginación de los datos en la tabla de los productos. Se hace referencia a un
   * componente `MatPaginator` que permite navegar entre las diferentes páginas de la tabla.
   */
  @ViewChild(MatPaginator) tablePaginator: MatPaginator | null = null;

  /**
   * Referencia al componente MatSort que se utiliza para habilitar la funcionalidad de ordenación en la tabla.
   *
   * Se utiliza el decorador `@ViewChild` para acceder a la instancia del componente MatSort asociado a la tabla de productos.
   * Esto permite aplicar la funcionalidad de ordenación en las columnas de la tabla, permitiendo a los usuarios ordenar los
   * datos de forma ascendente o descendente según la columna seleccionada.
   *
   * @see MatSort - Componente de Angular Material que proporciona capacidades de ordenación para las tablas.
   */
  @ViewChild(MatSort) tableSort: MatSort | null = null;

  /**
   * Constructor del componente.
   *
   * Este constructor inyecta las dependencias necesarias para el funcionamiento del componente.
   *
   * @param productService - Servicio para gestionar la información de los productos.
   * @param matDialog - Servicio para abrir cuadros de diálogo.
   * @param snackBar - Servicio para mostrar notificaciones de tipo snack bar.
   */
  constructor(
    private productService: ProductService,
    private matDialog: MatDialog,
    private snackBar: MatSnackBar
  ) {}

  /**
   * Método del ciclo de vida de Angular que se ejecuta una vez que el componente
   * ha sido inicializado, pero antes de que se renderice la vista en el DOM.
   *
   * Este método se utiliza para inicializar datos o realizar cualquier operación
   * que deba llevarse a cabo antes de que el componente sea visible para el usuario.
   * En este caso, se llama a la función `initTable()` para inicializar y cargar
   * los datos necesarios para la tabla.
   */
  ngOnInit(): void {
    this.initTable();
  }

  /**
   * Método del ciclo de vida de Angular que se ejecuta después de que
   * la vista del componente y sus vistas hijas hayan sido completamente inicializadas.
   *
   * Este método se utiliza para realizar inicializaciones adicionales que
   * requieren acceso a las vistas del componente (como elementos DOM,
   * directivas, o componentes hijos).
   */
  ngAfterViewInit(): void {
    this.configureTable();
  }

  /**
   * Inicializa la tabla de los productos.
   *
   * Este método se encarga de configurar y cargar los datos necesarios para la tabla de los productos.
   * Puede incluir la obtención de datos desde un servicio, la configuración de la paginación
   * y otras configuraciones relacionadas con la visualización de la tabla.
   */
  private async initTable(): Promise<void> {
    this.loading = true;
    this.tableData = new MatTableDataSource<IProduct>();

    try {
      // Obtener los productos y añadir-los a la tabla.
      const productResponse: IProductResponse =
        await this.productService.getAll();

      this.tableData = new MatTableDataSource<IProduct>(
        productResponse.products.map((product: IProduct) => {
          return new Product(product);
        })
      );
    } finally {
      this.configureTable();
      this.loading = false;
    }
  }

  /**
   * Configura la tabla de datos con las opciones necesarias, como la ordenación y la paginación.
   *
   * Se asegura de que la tabla esté configurada correctamente para su visualización y que las
   * funcionalidades de paginación y ordenación estén habilitadas, si es necesario.
   */
  private configureTable(): void {
    this.tableData.data?.reverse();

    // Asignar el `paginator` y el `sort` si existen.
    this.tablePaginator && (this.tableData.paginator = this.tablePaginator);
    this.tableSort && (this.tableData.sort = this.tableSort);
  }

  /**
   * Recarga la tabla de los productos.
   *
   * Esta función se encarga de actualizar la vista de la tabla de los productos
   * para reflejar los cambios recientes en los datos. Puede ser útil después
   * de realizar acciones como agregar, editar o eliminar productos, asegurando
   * que la interfaz siempre muestre la información más actualizada.
   */
  reload(): void {
    this.initTable();
  }

  /**
   * Crear un nuevo producto.
   *
   * Esta función abre un diálogo en la interfaz de usuario para permitir al
   * usuario ingresar los datos del nuevo producto. Una vez que el usuario
   * ha completado el formulario y confirmado la acción, la función recoge
   * la información ingresada y crea un nuevo registro de producto. Si la
   * creación es exitosa, se actualiza la lista de productos.
   */
  create(): void {
    this.matDialog
      .open(ProductCreateDialogComponent, {
        width: '500px',
        maxWidth: '100%',
      })
      .afterClosed()
      .subscribe((isCreated: boolean) => {
        if (isCreated) {
          this.initTable();
        }
      });
  }

  /**
   * Actualiza la información de un producto.
   *
   * Este método se encarga de actualizar los datos de un producto específica, generalmente a través
   * de una llamada a un servicio para realizar la actualización en la base de datos o en el servidor.
   * El parámetro `product` contiene los nuevos valores que se deben guardar.
   *
   * @param product - El objeto que contiene la información del producto.
   */

  update(product: IProduct) {
    this.matDialog
      .open(ProductUpdateDialogComponent, {
        width: '500px',
        maxWidth: '100%',
        data: product,
      })
      .afterClosed()
      .subscribe((isUpdated: boolean) => {
        if (isUpdated) {
          this.initTable();
        }
      });
  }

  /**
   * Elimina un producto de la lista.
   *
   * Abre un diálogo de confirmación para eliminar un producto de la lista.
   * Si el usuario confirma la acción, el producto es eliminado.
   *
   * @param product - El objeto producto que se va a eliminar.
   */
  delete(product: IProduct): void {
    this.matDialog
      .open(ProductDeleteDialogComponent, {
        width: '500px',
        maxWidth: '100%',
        data: product,
      })
      .afterClosed()
      .subscribe((isDeleted: boolean) => {
        if (isDeleted) {
          this.initTable();
        }
      });
  }

  /**
   * Actualiza el estado de `product.disabled` y lo guarda en la base de datos.
   * Revierte el cambio si ocurre un error.
   *
   * @param product - Objeto a actualizar.
   * @param isChecked - Nuevo estado del toggle.
   */
  async toggleStatus(product: IProduct, isChecked: boolean): Promise<void> {
    const previousValue = product.disabled;
    product.disabled = Number(!isChecked);

    try {
      const productResponse: string = await this.productService.update(product);
      this.snackBar.open(productResponse, '', {
        duration: 5000,
      });
    } catch (errors: any) {
      product.disabled = previousValue;
    }
  }
}
